import { useAction, useAtom } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'

import { AsyncStateN } from '@blade/std-lib'
import { Button, Swap2Icon } from '@blade/ui-kit'

import type { WalletSwapModel } from '../../atoms'
import type { ButtonProps } from '@blade/ui-kit'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  model: WalletSwapModel
  onSelectPair: () => void
} & ClassNameProps

export const WalletSwapButton: React.FC<Props> = ({
  className,
  onSelectPair,
  model,
}) => {
  const { t } = useTranslation()
  const [validationError] = useAtom(model.validation.validationErrorAtom)

  const [inCurrency] = useAtom(model.input.inCurrencyAtom)
  const [outCurrency] = useAtom(model.input.outCurrencyAtom)

  const [selectedPair] = useAtom(model.pair.selectedPoolAtom)
  const [pairsState] = useAtom(model.pair.poolsStateAtom)
  const [quoteState] = useAtom(model.quote.quoteStateAtom)

  const retryPairs = useAction(model.pair.retryLoadPoolsAction)
  const retryQuote = useAction(model.quote.forceStartPolling)
  const executeSwap = useAction(model.executeSwapAction)

  const baseProps = {
    className: className,
    fullWidth: true,
    radius: 'rounded',
    size: 'h36',
    variant: 'solid',
    color: 'blue',
  } satisfies ButtonProps

  if (!inCurrency || !outCurrency) {
    return (
      <Button {...baseProps} disabled>
        {t('trading.error.no-currency')}
      </Button>
    )
  }

  if (validationError?.type === 'LackOfFunds') {
    return (
      <Button {...baseProps} disabled>
        {t('trading.error.insufficient-funds', {
          ticker: inCurrency.ticker,
        })}
      </Button>
    )
  }
  if (validationError?.type === 'ZeroAmount') {
    return (
      <Button {...baseProps} disabled>
        {t('trading.error.no-amount')}
      </Button>
    )
  }

  if (!selectedPair) {
    if (AsyncStateN.isLoading(pairsState)) {
      return (
        <Button {...baseProps} disabled busy>
          {t('swap.loading-pools')}
        </Button>
      )
    }
    if (AsyncStateN.isSuccess(pairsState)) {
      if (pairsState.value.length === 0) {
        return (
          <Button {...baseProps} disabled>
            {t('swap.no-pool-available')}
          </Button>
        )
      }
      return (
        <Button {...baseProps} onClick={onSelectPair}>
          {t('swap.select-pool')}
        </Button>
      )
    }

    return (
      <Button {...baseProps} onClick={retryPairs}>
        {t('swap.load-pools')}
      </Button>
    )
  }

  if (AsyncStateN.isLoading(quoteState)) {
    return (
      <Button {...baseProps} disabled>
        {t('trading.quote-pending')}
      </Button>
    )
  }
  if (
    AsyncStateN.isError(quoteState) ||
    AsyncStateN.isNotInitiated(quoteState)
  ) {
    return (
      <Button {...baseProps} onClick={retryQuote}>
        {t('trading.quote-retry')}
      </Button>
    )
  }

  return (
    <Button
      {...baseProps}
      iconLeft={validationError ? undefined : <Swap2Icon />}
      onClick={() => {
        executeSwap()
      }}
      disabled={!!validationError || !AsyncStateN.isSuccess(quoteState)}
    >
      {t('swap')}
    </Button>
  )
}
